<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from '../buttons.constants';
import {
  baseClass,
  colorClass,
  iconSizeForButton,
  sizeClass,
} from '../buttons.styles';
import Icon from '../../icon/icon.vue';
import { IconName } from '../../icon/icon.constants';
import { useLink } from '../../../composables/use-link';

type Props = {
  /** Text label rendered inside the button. */
  label: string;
  /** Size of the button, controlling its height and font size. */
  size?: ButtonSize;
  /** Visual style variant (filled, outlined, or text). */
  variant?: ButtonVariant;
  /** Semantic color applied according to the chosen `variant`. */
  color?: ButtonColor;
  /** Whether the button expands to fill the full width of its container. */
  block?: boolean;
  /**
   * Native button `type` attribute. Applied only when the button renders as a
   * `<button>`; defaults to `ButtonType.BUTTON` when neither `to` nor `href` is set.
   */
  type?: ButtonType;
  /**
   * Router target. When set, the button renders as the host app's link
   * component (NuxtLink or RouterLink). Takes precedence over `href`.
   */
  to?: RouteLocationRaw;
  /**
   * External/plain URL. When set (and `to` is not), the button renders as an
   * `<a>` element.
   */
  href?: string;
  /**
   * Id of the `<form>` this button is associated with, forwarded to the
   * underlying element's native `form` attribute.
   */
  form?: string;
  /**
   * Glyph rendered before the label. For icon-only buttons use the
   * `IconButton` component.
   */
  leftIconName?: IconName;
  /** Glyph rendered after the label. */
  rightIconName?: IconName;
};

const props = withDefaults(defineProps<Props>(), {
  size: ButtonSize.MD,
  variant: ButtonVariant.FILLED,
  color: ButtonColor.PRIMARY,
});

// Host app supplies the link component (NuxtLink in app, RouterLink in admin).
const linkComponent = useLink();

const iconSize = computed(() => iconSizeForButton[props.size]);

const gapClass: Record<ButtonSize, string> = {
  [ButtonSize.SM]: 'gap-sm',
  [ButtonSize.MD]: 'gap-md',
  [ButtonSize.LG]: 'gap-md',
};

const btnClass = computed(() => {
  const classes = [
    ...baseClass,
    sizeClass[props.size],
    colorClass[props.variant][props.color],
    'px-2xl',
  ];

  if (props.leftIconName || props.rightIconName) {
    classes.push(gapClass[props.size]);
  }

  if (props.block) {
    classes.push('w-full');
  }

  return classes;
});

const tag = computed(() => {
  if (props.to !== undefined) {
    return linkComponent;
  }

  if (props.href !== undefined) {
    return 'a';
  }

  return 'button';
});

const parsedType = computed<ButtonType | null>(() => {
  if (props.type) {
    return props.type;
  }
  if (props.to === undefined && props.href === undefined) {
    return ButtonType.BUTTON;
  }

  return null;
});
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :type="parsedType"
    :href="href"
    :form="props.form"
    :class="btnClass"
  >
    <Icon v-if="leftIconName" :name="leftIconName" :size="iconSize" />
    {{ label }}
    <Icon v-if="rightIconName" :name="rightIconName" :size="iconSize" />
  </component>
</template>
